import React from "react";
import { Box, CircularProgress, Link, Typography, TypographyProps } from "@mui/material";

interface CustomerNameLinkProps {
    /** The linked customer, if any — walk-in/guest orders have none. */
    customerId?: string | null;
    name?: string | null;
    /** Opens the customer's details (see useCustomerDetails). */
    onOpen: (customerId: string) => void;
    /** This customer is being loaded — shows a small spinner. */
    loading?: boolean;
    variant?: TypographyProps["variant"];
    fontWeight?: TypographyProps["fontWeight"];
    noWrap?: boolean;
}

/**
 * A customer's name that opens their details. Plain text when there's no
 * linked customer (shown as "Guest" if the name is missing too).
 */
export const CustomerNameLink: React.FC<CustomerNameLinkProps> = ({
    customerId,
    name,
    onOpen,
    loading = false,
    variant = "body2",
    fontWeight,
    noWrap,
}) => {
    const label = name || "Guest";
    if (!customerId) {
        return (
            <Typography variant={variant} fontWeight={fontWeight} noWrap={noWrap}>
                {label}
            </Typography>
        );
    }
    return (
        <Link
            component="button"
            type="button"
            variant={variant}
            fontWeight={fontWeight}
            underline="hover"
            onClick={(e: React.MouseEvent) => {
                e.stopPropagation(); // not a row/card click
                onOpen(customerId);
            }}
            title="View customer details"
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                maxWidth: "100%",
                minWidth: 0,
                textAlign: "left",
                verticalAlign: "baseline",
            }}
        >
            <Box
                component="span"
                sx={
                    noWrap
                        ? { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }
                        : undefined
                }
            >
                {label}
            </Box>
            {loading && <CircularProgress size={12} color="inherit" sx={{ flexShrink: 0 }} />}
        </Link>
    );
};
